import React, { useState, useEffect, Suspense, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import axios from "axios";
import config from "../../config.js";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { CircularProgress } from "@mui/material";
import DeleteModal from "../common/DeleteModal";
import { useTranslation } from "react-i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { formatDate, formatTime } from "../../utils/formatDate";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  FLEET_SCHEDULE_ROW_ACTIONS,
  computeFleetScheduleMetric,
} from "../../config/schedulesModuleCatalog";
import { hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFleetRowActionsFromLayout,
  buildFleetScheduleStatCards,
  buildFilterWidgetsFromLayout,
  buildTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
} from "../../layout/moduleLayoutUtils";
const DynamicTable = React.lazy(() => import("../common/DynamicTable"));

const legacyHeaders = [
  { key: "Name", value: "Schedules" },
  { key: "Description", value: "Description" },
  { key: "Start Date", value: "Start Date" },
  { key: "Start Time", value: "Start Time" },
  { key: "End Time", value: "End Time" },
  { key: "Repetition", value: "Repetition" },
  { key: "Route", value: "Route" },
  { key: "Vehicle", value: "Vehicle" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

export default function ScheduleMain({ school = false } = {}) {
  const { t } = useTranslation();
  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;
  const [scheduleList, setScheduleList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    startTime: "",
    repetition: "",
    routeName: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const schedulesPageId = school ? PAGE_IDS.SCHOOL_SCHEDULES : PAGE_IDS.FLEET_SCHEDULES;
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? schedulesPageId : null,
  );

  const layoutHeaders = useMemo(() => {
    if (!moduleLayout) return null;
    return buildTableHeadersFromLayout(moduleLayout, { hasPermission, t });
  }, [moduleLayout, hasPermission, t]);

  const layoutRowActions = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetRowActionsFromLayout(
      moduleLayout,
      { hasPermission },
      FLEET_SCHEDULE_ROW_ACTIONS,
    );
  }, [moduleLayout, hasPermission]);

  const layoutStatCards = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetScheduleStatCards(moduleLayout, scheduleList);
  }, [moduleLayout, scheduleList]);

  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout) : []),
    [moduleLayout],
  );

  const layoutFilterWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFilterWidgetsFromLayout(moduleLayout);
  }, [moduleLayout]);

  const tableHeaders = layoutHeaders || legacyHeaders;
  const downloadColumns = tableHeaders
    .filter((header) => header.key !== ACTIONS_COLUMN_ID)
    .map((header) => ({
      key: header.key,
      label: header.value,
    }));

  const getLegacyActions = () =>
    Object.values(FLEET_SCHEDULE_ROW_ACTIONS).filter((action) =>
      hasLayoutPermission(action.permission, hasPermission),
    );

  const tableActions = layoutRowActions || getLegacyActions();

  const [startTimeFilterOptions, setStartTimeFilterOptions] = useState([]);
  const [routeNameFilterOptions, setRouteNameFilterOptions] = useState([]);

  const formatRepetition = (repetition) => {
    if (!repetition) return "N/A";

    // Handle the special case for "ONETIME"
    if (repetition.toUpperCase() === "ONETIME") {
      return "One time";
    }

    // Convert "DAILY" to "Daily", "WEEKLY" to "Weekly", etc.
    return (
      repetition.charAt(0).toUpperCase() + repetition.slice(1).toLowerCase()
    );
  };

  // Format ISO date string to 12-hour time with AM/PM (for startTime and endTime)
  // Format ISO date string to include both date and time in 12-hour format with AM/PM
  const formatIsoTime = (isoTimeStr) => {
    if (!isoTimeStr) return "N/A";
    const date = new Date(isoTimeStr);
    if (isNaN(date.getTime())) return "Invalid Date";
    const datePart = date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const timePart = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    // return `${datePart} ${timePart}`;
    return `${timePart}`;
  };

  const [filterConfig, setFilterConfig] = useState([
    {
      id: "repetition", // Fixed typo: repitation -> repetition
      label: "Repetition",
      helperText: "Select repetition type",
      options: [
        { value: "DAILY", label: "Daily" },
        { value: "WEEKLY", label: "Weekly" },
        { value: "MONTHLY", label: "Monthly" },
        { value: "ONETIME", label: "One time" },
      ],
    },
    {
      id: "startTime",
      label: "Start Time",
      helperText: "Select start time",
      options: [],
    },
    {
      id: "routeName",
      label: "Routes",
      helperText: "Select route",
      options: [],
    },
  ]);

  useEffect(() => {
    if (layoutFilterWidgets) {
      setFilterConfig(
        layoutFilterWidgets.map((widget) => ({
          id: widget.filterKey,
          label: widget.labelKey,
          helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
          options:
            widget.filterKey === "startTime"
              ? startTimeFilterOptions
              : widget.filterKey === "routeName"
                ? routeNameFilterOptions
                : (widget.options || []).map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
        })),
      );
      return;
    }

    setFilterConfig([
      {
        id: "repetition",
        label: "Repetition",
        helperText: "Select repetition type",
        options: [
          { value: "DAILY", label: "Daily" },
          { value: "WEEKLY", label: "Weekly" },
          { value: "MONTHLY", label: "Monthly" },
          { value: "ONETIME", label: "One time" },
        ],
      },
      {
        id: "startTime",
        label: "Start Time",
        helperText: "Select start time",
        options: startTimeFilterOptions,
      },
      {
        id: "routeName",
        label: "Routes",
        helperText: "Select route",
        options: routeNameFilterOptions,
      },
    ]);
  }, [layoutFilterWidgets, startTimeFilterOptions, routeNameFilterOptions]);

  async function getAllSchedules() {
    if (!orgId) return [];

    setIsLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/schedule/organization`, {
        params: { organizationId: orgId },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in fetching schedule data: ", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  // Function to delete schedule
  async function deleteSchedule(id) {
    try {
      const response = await axios.delete(`${config.baseURL}/schedule`, {
        data: { scheduleId: Number(id) },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in deleting schedule data: ", error);
      throw error;
    }
  }

  // Fetch schedules data on component mount
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const schedules = await getAllSchedules();
        setScheduleList(schedules);

        // Extract unique start times for filter
        const startTimes = [];
        const startTimeSet = new Set();
        schedules.forEach((schedule) => {
          if (schedule.startTime && !startTimeSet.has(schedule.startTime)) {
            startTimeSet.add(schedule.startTime);
            startTimes.push({
              value: schedule.startTime,
              label: formatIsoTime(schedule.startTime),
            });
          }
        });

        // Extract unique routes for filter
        const routes = [];
        const routeSet = new Set();
        schedules.forEach((schedule) => {
          if (
            schedule.route &&
            schedule.route.name &&
            !routeSet.has(schedule.route.name)
          ) {
            routeSet.add(schedule.route.name);
            routes.push({
              value: schedule.route.name,
              label: schedule.route.name,
            });
          }
        });

        setStartTimeFilterOptions(startTimes);
        setRouteNameFilterOptions(routes);
      } catch (error) {
        console.error("Error fetching schedule data:", error);
      }
    };

    fetchSchedules();
  }, [navigate, orgId]);

  // Format data for table display
  const formatDataForTable = (data) => {
    return data.map((schedule) => {
      const formattedData = {
        Name: schedule.name,
        Description: schedule.description,
        "Start Date": formatDate(schedule.startTime, false, false),
        "Start Time": formatTime(schedule.startTime),
        "End Time": formatTime(schedule.endTime),
        Repetition: formatRepetition(
          schedule.repetition || schedule.repitation
        ),
        Route: schedule.route ? schedule.route.name : "N/A",
        Vehicle: schedule.vehicleSchedule
          ? schedule.vehicleSchedule.map((vs) => vs.vehicle.name).join(", ")
          : "N/A",
      };

      // Add original ID for action handling
      formattedData.id = schedule.id;
      formattedData.originalData = schedule;

      return formattedData;
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  // Filter data based on search query and filters
  const filteredScheduleData = scheduleList.filter((schedule) => {
    // Get the repetition value (handle typo from backend)
    const repetitionValue = schedule.repetition || schedule.repitation;

    // Check if repetition filter is applied
    if (filters.repetition && repetitionValue !== filters.repetition) {
      return false;
    }

    // Check if start time filter is applied
    if (filters.startTime && schedule.startTime !== filters.startTime) {
      return false;
    }

    // Check if route name filter is applied
    if (
      filters.routeName &&
      schedule.route &&
      schedule.route.name !== filters.routeName
    ) {
      return false;
    }

    // If no search query, return true (show all that match filters)
    if (!searchQuery) {
      return true;
    }

    // Otherwise, search in all relevant fields
    const query = searchQuery.toLowerCase();
    const startDateStr = formatDate(schedule.startTime, false, false) || "";
    const startTimeStr = formatTime(schedule.startTime) || "";
    const endTimeStr = formatTime(schedule.endTime) || "";
    const vehicleStr = schedule.vehicleSchedule
      ? schedule.vehicleSchedule.map((vs) => vs.vehicle.name).join(", ")
      : "";
    return (
      (schedule.name && schedule.name.toLowerCase().includes(query)) ||
      (schedule.description &&
        schedule.description.toLowerCase().includes(query)) ||
      startDateStr.toLowerCase().includes(query) ||
      startTimeStr.toLowerCase().includes(query) ||
      endTimeStr.toLowerCase().includes(query) ||
      (repetitionValue && formatRepetition(repetitionValue).toLowerCase().includes(query)) ||
      (schedule.route &&
        schedule.route.name &&
        schedule.route.name.toLowerCase().includes(query)) ||
      vehicleStr.toLowerCase().includes(query)
    );
  });

  // Add schedule handler
  const handleAddSchedule = () => {
    if (!hasPermission("/api/schedule", "POST")) {
      return;
    }
    navigate(school ? "/school-schedules/add" : "/schedule/add");
  };

  const handleActionClick = (actionId, rowIndex, actionObj) => {
    const schedule =
      filteredScheduleData[rowIndex].originalData ||
      filteredScheduleData[rowIndex];

    switch (actionId) {
      // case "view":
      //     // Navigate to view mode (read-only)
      //     navigate(`/schedule/${schedule.id}`, { state: { mode: 'view' } });
      //     break;
      case "update":
        if (hasPermission("/api/schedule", "PUT")) {
          navigate(school ? `/school-schedules/${schedule.id}` : `/schedule/${schedule.id}`);
        }
        break;
      case "delete":
        if (hasPermission("/api/schedule", "DELETE")) {
          setScheduleToDelete(schedule);
          setDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      if (scheduleToDelete) {
        console.log("Deleting schedule ID:", scheduleToDelete.id);
        await deleteSchedule(scheduleToDelete.id);
        setScheduleList((prevList) =>
          prevList.filter((schedule) => schedule.id !== scheduleToDelete.id)
        );
      }
    } catch (error) {
      console.error("Error deleting schedule:", error);
    } finally {
      setDeleteModalOpen(false);
      setScheduleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setScheduleToDelete(null);
  };

  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createSchedule");
  const canCreateSchedule = createAction
    ? hasLayoutPermission(createAction.permission, hasPermission)
    : hasPermission("/api/schedule", "POST");
  const pageTitle = getPageTitleKey(moduleLayout) || "schedules";
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;

  const legacyStatCards = [
    {
      title: "totalSchedules",
      value: computeFleetScheduleMetric("totalSchedules", scheduleList),
      backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "tripsinNext1Hour",
      value: computeFleetScheduleMetric("tripsInNextHour", scheduleList),
      backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
  ];

  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title={pageTitle}
        path={!school ? `${orgName || "organization"} > schedules` : undefined}
        add={!organizationInactive && canCreateSchedule ? createAction?.labelKey || "createSchedule" : null}
        addPath={school ? "/school-schedules/add" : "/schedule/add"}
        onAddClick={!organizationInactive && canCreateSchedule ? handleAddSchedule : null}
      />

      {organizationInactive ? (
        <div className="table_container">
          <InactiveInventoryTable
            headers={tableHeaders}
            data={formatDataForTable(filteredScheduleData || [])}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            itemLabel="schedules"
          />
        </div>
      ) : (
        <>
          <Cards cardData={layoutStatCards || legacyStatCards} />

          <div className="table_container">
            <SearchFilter
              filters={filterConfig}
              searchPlaceholder="search"
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              dataDownload={true}
              downloadRows={formatDataForTable(filteredScheduleData || [])}
              downloadColumns={downloadColumns}
              downloadFileName={school ? "school-schedules" : "schedules"}
            />

        <Suspense fallback={<CircularProgress />}>
          <DynamicTable
            headers={tableHeaders}
            data={formatDataForTable(filteredScheduleData || [])}
            onActionClick={handleActionClick}
            actions={tableActions}
            loading={isLoading}
          />
        </Suspense>
      </div>
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Schedule!"
        message={`${t("Are you sure you want to delete")} ${
          scheduleToDelete?.name || "this schedule"
        }? ${t("This action cannot be undone.")}`}
      />
    </div>
  );
}
